import {
  ArrowLeftOutlined,
  BankOutlined,
  PlusOutlined,
} from "@ant-design/icons";
import {
  Avatar,
  Breadcrumb,
  Button,
  Layout,
  Skeleton,
  Typography,
} from "antd";
import React, { memo, useCallback, useEffect, useState, Suspense, lazy } from "react";
import { UserAvatar } from "@/shared/components/UserAvatar.js";

const StockAssetFlowPage = lazy(() =>
  import("@/features/stock/presentation/pages/StockAssetFlowPage.js").then((m) => ({
    default: m.StockAssetFlowPage,
  }))
);
const StockAssetTreemapPage = lazy(() =>
  import("@/features/stock/presentation/pages/StockAssetTreemapPage.js").then((m) => ({
    default: m.StockAssetTreemapPage,
  }))
);
import {
  Navigate,
  Outlet,
  Route,
  Routes,
  useLocation,
  useNavigate,
} from "react-router-dom";
import { useAppState } from "@/core/app_state/AppStateContext";
import { useAcl } from "@/core/acl/useAcl";
import { NavHistoryProvider, useNavHistory } from "@/core/nav/NavHistoryContext";
import { AppShellLayoutContext } from "@/core/layout/AppShellLayoutContext.js";
import { useAuthSession } from "@/features/authentication/presentation/hooks/useAuth";
import { logAuthEvent } from "@/core/debug/authDebugLog.js";
const LoginPage = lazy(() => import("@/features/authentication/presentation/pages/LoginPage").then((m) => ({ default: m.LoginPage })));
const OrgBiDashboardPage = lazy(() => import("@/features/bi/presentation/pages/OrgBiDashboardPage.js").then((m) => ({ default: m.OrgBiDashboardPage })));
const FormxDetailPage = lazy(() => import("@/features/formx/presentation/pages/FormxDetailPage").then((m) => ({ default: m.FormxDetailPage })));
const FormxFieldDataPage = lazy(() => import("@/features/formx/presentation/pages/FormxFieldDataPage").then((m) => ({ default: m.FormxFieldDataPage })));
const FormxListPage = lazy(() => import("@/features/formx/presentation/pages/FormxListPage").then((m) => ({ default: m.FormxListPage })));
const IssueListPage = lazy(() => import("@/features/issue/presentation/pages/IssueListPage").then((m) => ({ default: m.IssueListPage })));
const KpiListPage = lazy(() => import("@/features/kpi/presentation/pages/KpiListPage").then((m) => ({ default: m.KpiListPage })));
const NotificationPage = lazy(() => import("@/features/notification/presentation/pages/NotificationPage.js").then((m) => ({ default: m.NotificationPage })));
const LocationDetailPage = lazy(() => import("@/features/location/presentation/pages/LocationDetailPage").then((m) => ({ default: m.LocationDetailPage })));
const LocationListPage = lazy(() => import("@/features/location/presentation/pages/LocationListPage").then((m) => ({ default: m.LocationListPage })));
const InviteRedeemPage = lazy(() => import("@/features/membership/presentation/pages/InviteRedeemPage").then((m) => ({ default: m.InviteRedeemPage })));
const MemberListPage = lazy(() => import("@/features/membership/presentation/pages/MemberListPage").then((m) => ({ default: m.MemberListPage })));
const MetadataListPage = lazy(() => import("@/features/metadata/presentation/pages/MetadataListPage").then((m) => ({ default: m.MetadataListPage })));
const OrgListPage = lazy(() => import("@/features/org/presentation/pages/OrgListPage").then((m) => ({ default: m.OrgListPage })));
const ProjectDetailPage = lazy(() => import("@/features/project/presentation/pages/ProjectDetailPage").then((m) => ({ default: m.ProjectDetailPage })));
const ProjectListPage = lazy(() => import("@/features/project/presentation/pages/ProjectListPage").then((m) => ({ default: m.ProjectListPage })));
const SessionListPage = lazy(() => import("@/features/session/presentation/pages/SessionListPage").then((m) => ({ default: m.SessionListPage })));
const PayoutInboxPage = lazy(() => import("@/features/payout/presentation/pages/PayoutInboxPage").then((m) => ({ default: m.PayoutInboxPage })));
const AdminPayoutDetailPage = lazy(() => import("@/features/payout/presentation/pages/AdminPayoutDetailPage").then((m) => ({ default: m.AdminPayoutDetailPage })));
const AccountantPayoutDetailPage = lazy(() => import("@/features/payout/presentation/pages/AccountantPayoutDetailPage").then((m) => ({ default: m.AccountantPayoutDetailPage })));
const ExperimentalMobileSimPage = lazy(() => import("@/features/payout/presentation/pages/ExperimentalMobileSimPage").then((m) => ({ default: m.ExperimentalMobileSimPage })));
const PayoutReceiptUploadPage = lazy(() => import("@/features/payout/presentation/pages/PayoutReceiptUploadPage").then((m) => ({ default: m.PayoutReceiptUploadPage })));
const PublicReceiptPage = lazy(() => import("@/features/payout/presentation/pages/PublicReceiptPage").then((m) => ({ default: m.PublicReceiptPage })));
const ContactListPage = lazy(() => import("@/features/contacts/presentation/pages/ContactListPage").then((m) => ({ default: m.ContactListPage })));
const AuditLogListPage = lazy(() => import("@/features/auditLog/presentation/pages/AuditLogListPage").then((m) => ({ default: m.AuditLogListPage })));
const AssessmentListPage = lazy(() => import("@/features/assessment/presentation/pages/AssessmentListPage").then((m) => ({ default: m.AssessmentListPage })));
const AssessmentBuilderPage = lazy(() => import("@/features/assessment/presentation/pages/AssessmentBuilderPage").then((m) => ({ default: m.AssessmentBuilderPage })));
const ExportAccessPage = lazy(() => import("@/features/exportAccess/presentation/pages/ExportAccessPage").then((m) => ({ default: m.ExportAccessPage })));
const ConfigurationAdminPage = lazy(() => import("@/features/configuration/presentation/pages/ConfigurationAdminPage").then((m) => ({ default: m.ConfigurationAdminPage })));
const SuperAdminHubPage = lazy(() => import("@/features/superAdmin/presentation/pages/SuperAdminHubPage").then((m) => ({ default: m.SuperAdminHubPage })));
const ForceSyncPage = lazy(() => import("@/features/superAdmin/presentation/pages/ForceSyncPage").then((m) => ({ default: m.ForceSyncPage })));
const SystemConfigPage = lazy(() => import("@/features/superAdmin/presentation/pages/SystemConfigPage").then((m) => ({ default: m.SystemConfigPage })));
const StockListPage = lazy(() => import("@/features/stock/presentation/pages/StockListPage").then((m) => ({ default: m.StockListPage })));
const SourcingPage = lazy(() => import("@/features/sourcing/presentation/pages/SourcingPage").then((m) => ({ default: m.SourcingPage })));
const UserDetailPage = lazy(() => import("@/features/user/presentation/pages/UserDetailPage").then((m) => ({ default: m.UserDetailPage })));
const UserListPage = lazy(() => import("@/features/user/presentation/pages/UserListPage").then((m) => ({ default: m.UserListPage })));
const ToolsHubPage = lazy(() => import("@/features/tools/presentation/pages/ToolsHubPage").then((m) => ({ default: m.ToolsHubPage })));
const AddressFixerPage = lazy(() => import("@/features/tools/addressFixer/presentation/pages/AddressFixerPage").then((m) => ({ default: m.AddressFixerPage })));

const TestComponent = lazy(() => import("@/shared/components/TestComponent").then((m) => ({ default: m.TestComponent })));
const TimeRangeSliderDemo = lazy(() => import("@/shared/components/experimental/TimeRangeSliderDemo").then((m) => ({ default: m.TimeRangeSliderDemo })));
const WardSelectDemo = lazy(() => import("@/shared/components/experimental/WardSelectDemo").then((m) => ({ default: m.WardSelectDemo })));
const ExperimentalHubPage = lazy(() => import("@/shared/components/experimental/ExperimentalHubPage").then((m) => ({ default: m.ExperimentalHubPage })));
const LocationTabDesignPage = lazy(() => import("@/shared/components/experimental/LocationTabDesign").then((m) => ({ default: m.LocationTabDesignPage })));
const ProjectDetailDesignPage = lazy(() => import("@/shared/components/experimental/ProjectDetailDesign").then((m) => ({ default: m.ProjectDetailDesignPage })));
const SidebarDesignPage = lazy(() => import("@/shared/components/experimental/SidebarDesign").then((m) => ({ default: m.SidebarDesignPage })));
const ServerReportExportPage = lazy(() => import("@/shared/components/experimental/ServerReportExportPage").then((m) => ({ default: m.ServerReportExportPage })));
const SessionTimelineDesign = lazy(() => import("@/shared/components/experimental/SessionTimelineDesign").then((m) => ({ default: m.SessionTimelineDesign })));
const SessionGanttDesign = lazy(() => import("@/shared/components/experimental/SessionGanttDesign").then((m) => ({ default: m.SessionGanttDesign })));
const LocationSessionCards = lazy(() => import("@/shared/components/experimental/LocationSessionCards").then((m) => ({ default: m.LocationSessionCards })));
const ProjectListCardView = lazy(() => import("@/shared/components/experimental/ProjectListCardView").then((m) => ({ default: m.ProjectListCardView })));
const ExcelImportMapperPage = lazy(() => import("@/shared/components/experimental/ExcelImportMapperPage").then((m) => ({ default: m.ExcelImportMapperPage })));

import { ErrorPage } from "@/shared/components/ErrorPage";
import { GlobalSearch } from "@/shared/components/GlobalSearch";
import { NotificationBell } from "@/shared/components/NotificationBell";
import { useWebPush } from "@/features/notification/presentation/hooks/useWebPush.js";
import { useServiceWorkerUpdate } from "@/core/hooks/useServiceWorkerUpdate.js";

const TEST_ERROR_ROUTE_ENABLED =
  (import.meta as ImportMeta & { env: Record<string, string> }).env
    .VITE_ENABLE_TEST_ERROR_ROUTE === "true";
import {
  AppSidebar,
  MobileBottomNav,
  ROOT_PATHS,
  SIDEBAR_WIDTH,
  SIDEBAR_COLLAPSED_WIDTH,
  TOPBAR_HEIGHT,
  avatarStyle,
  resolveMenuKey,
} from "@/router/AppSidebar";

const { Header, Content } = Layout;
const { Text, Title } = Typography;

const ROUTE_LABELS: Record<string, string> = {
  "": "Dashboard",
  orgs: "Tổ chức",
  projects: "Dự án",
  locations: "Địa điểm",
  users: "Người dùng",
  issues: "Issues",
  stock: "Kho hàng",
  sessions: "Sessions",
  kpis: "KPIs",
  formx: "FormX",
  metadata: "Danh mục",
  members: "Thành viên",
  overview: "Tổng quan",
  info: "Thông tin",
  appearance: "Giao diện",
  permissions: "Quyền hạn",
  fields: "Trường dữ liệu",
  submissions: "Phản hồi",
  "audit-log": "Nhật ký",
};

const UUID_RE =
  /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

function AppBreadcrumb() {
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const { entries } = useNavHistory();

  if (entries.length === 0) return null;

  type Crumb = { title: React.ReactNode };
  const crumbs: Crumb[] = [];
  const n = entries.length;
  const goTo = (idx: number) => {
    const delta = idx - (n - 1);
    if (delta < 0) navigate(delta);
  };
  const toCrumb = (e: typeof entries[number], idx: number, isLast: boolean): Crumb =>
    isLast
      ? { title: <span style={{ color: "var(--text)" }}>{e.label}</span> }
      : {
        title: (
          <a
            onClick={(ev) => {
              ev.preventDefault();
              goTo(idx);
            }}
            style={{ cursor: "pointer" }}
          >
            {e.label}
          </a>
        ),
      };

  if (n <= 5) {
    entries.forEach((e, i) => crumbs.push(toCrumb(e, i, i === n - 1)));
  } else {
    crumbs.push(toCrumb(entries[0]!, 0, false));
    crumbs.push(toCrumb(entries[1]!, 1, false));
    crumbs.push({ title: "…" });
    crumbs.push(toCrumb(entries[n - 2]!, n - 2, false));
    crumbs.push(toCrumb(entries[n - 1]!, n - 1, true));
  }

  const handleBack = () => {
    if (entries.length > 1) navigate(-1);
    else navigate("/");
  };

  return (
    <div
      style={{
        display: "flex",
        alignItems: "center",
        gap: 12,
        marginBottom: 16,
      }}
    >
      {pathname !== "/stock/treemap" && <Button
        type="text"
        size="small"
        icon={<ArrowLeftOutlined />}
        onClick={handleBack}
        style={{
          color: "var(--text2)",
          padding: 0,
          width: 28,
          height: 28,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      />}
      <Breadcrumb
        items={crumbs}
        style={{ fontSize: 12, opacity: 0.7, marginBottom: 0 }}
      />
    </div>
  );
}

const headerStyle: React.CSSProperties = {
  background: "var(--bg2)",
  borderBottom: "1px solid var(--border)",
  height: TOPBAR_HEIGHT,
  lineHeight: `${TOPBAR_HEIGHT}px`,
  padding: "0 20px",
  position: "sticky",
  top: 0,
  zIndex: 99,
  display: "flex",
  alignItems: "center",
  gap: 12,
};

const topbarRightStyle: React.CSSProperties = {
  marginLeft: "auto",
  display: "flex",
  alignItems: "center",
  gap: 16,
  flexShrink: 0,
};

const avatarWrapStyle: React.CSSProperties = {
  display: "flex",
  alignItems: "center",
  gap: 8,
  cursor: "pointer",
  minWidth: 0,
  maxWidth: 160,
};

// ── Topbar ───────────────────────────────────────────────────────────────────

const AppTopbar = memo(function AppTopbar({
  userId,
  isMobile,
}: {
  userId: string;
  isMobile: boolean;
}) {
  const { activeOrg } = useAppState();
  const navigate = useNavigate();

  const handleAvatarClick = useCallback(() => {
    navigate(`/users/${userId}`);
  }, [navigate, userId]);

  const { data: session } = useAuthSession();
  const userInitial = session?.email?.[0]?.toUpperCase() ?? "U";

  return (
    <Header style={headerStyle}>
      {activeOrg && !isMobile && (
        <Text
          style={{
            color: "var(--text3)",
            fontSize: 13,
            whiteSpace: "nowrap",
            overflow: "hidden",
            textOverflow: "ellipsis",
            minWidth: 0,
            maxWidth: 200,
          }}
        >
          {activeOrg.name}
        </Text>
      )}
      {activeOrg && <GlobalSearch />}
      <div style={topbarRightStyle}>
        {!isMobile && (
          <Text style={{ color: "var(--text3)", fontSize: 11, whiteSpace: "nowrap" }}>
            {__BUILD_VERSION__}
          </Text>
        )}
        <NotificationBell />
        {/* Desktop profile lives in the sidebar footer; only mobile needs it here. */}
        {isMobile && (
          <div style={avatarWrapStyle} onClick={handleAvatarClick}>
            <UserAvatar
              size={30}
              style={avatarStyle}
              user={{
                id: userId,
                email: session?.email,
                avatarUrl: session?.photoURL,
              }}
            >
              {userInitial}
            </UserAvatar>
          </div>
        )}
      </div>
    </Header>
  );
});

// ── Empty org state ───────────────────────────────────────────────────────────

function EmptyOrgState() {
  const navigate = useNavigate();
  const { isSuperAdmin } = useAcl();
  return (
    <div
      style={{
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        minHeight: 400,
        gap: 16,
        textAlign: "center",
      }}
    >
      <BankOutlined style={{ fontSize: 48, color: "var(--text3)" }} />
      <Title level={4} className="t-primary m-0">
        Chưa có tổ chức nào
      </Title>
      <Text className="t-secondary">Tạo tổ chức để bắt đầu sử dụng HIMC</Text>
      {isSuperAdmin && (
        <Button
          type="primary"
          icon={<PlusOutlined />}
          onClick={() => navigate("/orgs")}
        >
          Tạo tổ chức
        </Button>
      )}
    </div>
  );
}

// ── Shell ─────────────────────────────────────────────────────────────────────

function ProtectedRoute() {
  const location = useLocation();
  const { data: session, isLoading } = useAuthSession();

  if (isLoading) return null;
  if (!session) {
    logAuthEvent('ProtectedRoute:redirect-to-login', {
      from: location.pathname,
      visibility: typeof document !== 'undefined' ? document.visibilityState : undefined,
    });
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  return <Outlet />;
}

function SuperAdminRoute() {
  const { isSuperAdmin, isLoading } = useAcl();

  if (isLoading) return null;
  if (!isSuperAdmin) return <Navigate to="/" replace />;

  return <Outlet />;
}

function AppShell() {
  const { data: session } = useAuthSession();
  const { orgs, orgsLoading } = useAppState();
  const location = useLocation();
  const MOBILE_BREAKPOINT = 1000;
  const [isMobile, setIsMobile] = useState(
    () => window.innerWidth <= MOBILE_BREAKPOINT,
  );
  const [manualCollapsed, setManualCollapsed] = useState(false);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [sidebarReservation, setSidebarReservation] = useState(0);

  useEffect(() => {
    const handleResize = () => {
      const mobile = window.innerWidth <= MOBILE_BREAKPOINT;
      setIsMobile(mobile);
      if (mobile) setDrawerOpen(false);
    };
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  // Register this browser for FCM web push + keep the bell live (no-op until logged in).
  useWebPush(session?.uid);

  // Register the app shell service worker + prompt on new deploys (registerType: 'prompt').
  useServiceWorkerUpdate();

  const isLoginPage = location.pathname === "/login";
  const isInvitePage = location.pathname.startsWith("/invite/");
  const isUploadPage = location.pathname.startsWith("/payout-upload");
  const isPublicReceipt =
    location.pathname === "/r" || location.pathname.startsWith("/r/");
  const isFullscreenPage = /^\/stock\/assets\/[^/]+\/flow/.test(location.pathname);

  if (isLoginPage || isInvitePage || isUploadPage || isPublicReceipt || !session) {
    return (
      <Layout style={{ minHeight: "100vh" }}>
        <Content>
          <Outlet />
        </Content>
      </Layout>
    );
  }

  const sidebarWidth = isMobile
    ? 0
    : Math.max(
        sidebarReservation,
        manualCollapsed ? SIDEBAR_COLLAPSED_WIDTH : SIDEBAR_WIDTH,
      );

  return (
    <AppShellLayoutContext.Provider value={{ setSidebarReservation }}>
      <NavHistoryProvider routeLabels={ROUTE_LABELS} rootPaths={ROOT_PATHS}>
        <Layout style={{ minHeight: "100vh" }}>
          <AppSidebar
          selectedKey={resolveMenuKey(location.pathname)}
          isMobile={isMobile}
          collapsed={manualCollapsed}
          onUserCollapse={setManualCollapsed}
          drawerOpen={drawerOpen}
          onDrawerClose={() => setDrawerOpen(false)}
        />
        <Layout
          style={{
            marginLeft: sidebarWidth,
            transition: "margin-left 0.2s",
            minWidth: 0,
            flex: "1 1 auto",
          }}
        >
          <AppTopbar userId={session.uid} isMobile={isMobile} />
          <Content
            style={{
              padding: isFullscreenPage
                ? 0
                : isMobile
                  ? "12px 12px calc(72px + env(safe-area-inset-bottom))"
                  : 24,
              minHeight: `calc(100vh - ${TOPBAR_HEIGHT}px)`,
              minWidth: 0,
              width: "100%",
              overflowX: "clip",
              background: "var(--bg)",
              display: isFullscreenPage ? "flex" : undefined,
              flexDirection: isFullscreenPage ? "column" : undefined,
            }}
          >
            {!orgsLoading && orgs.length === 0 && location.pathname !== "/orgs" ? (
              <EmptyOrgState />
            ) : isFullscreenPage ? (
              <Outlet />
            ) : (
              <>
                <AppBreadcrumb />
                <Outlet />
              </>
            )}
          </Content>
        </Layout>
        {isMobile && (
          <MobileBottomNav
            selectedKey={location.pathname}
            onMore={() => setDrawerOpen(true)}
          />
        )}
      </Layout>
      </NavHistoryProvider>
    </AppShellLayoutContext.Provider>
  );
}

// ── Router ────────────────────────────────────────────────────────────────────

export function AppRouter() {
  return (
    <Suspense fallback={<Skeleton active paragraph={{ rows: 8 }} style={{ padding: 24 }} />}>
      <Routes>
      <Route element={<AppShell />}>
        <Route path="/login" element={<LoginPage />} />
        <Route path="/invite" element={<InviteRedeemPage />} />
        <Route path="/invite/:code" element={<InviteRedeemPage />} />
        <Route path="/payout-upload" element={<PayoutReceiptUploadPage />} />
        <Route path="/r" element={<PublicReceiptPage />} />
        <Route path="/r/:code" element={<PublicReceiptPage />} />
        <Route element={<ProtectedRoute />}>
          <Route path="/" element={<OrgBiDashboardPage />} />
          <Route path="/bi" element={<OrgBiDashboardPage />} />
          <Route path="/projects" element={<ProjectListPage />} />
          <Route path="/projects/:id/:tab?" element={<ProjectDetailPage />} />
          <Route path="/issues" element={<IssueListPage />} />
          <Route path="/notifications" element={<NotificationPage />} />
          <Route path="/kpis" element={<KpiListPage />} />
          <Route path="/members" element={<MemberListPage />} />
          <Route path="/metadata" element={<MetadataListPage />} />
          <Route path="/locations" element={<LocationListPage />} />
          <Route path="/locations/:id/:tab?" element={<LocationDetailPage />} />
          <Route path="/sessions" element={<SessionListPage />} />
          <Route path="/stock" element={<StockListPage />} />
          <Route
            path="/stock/treemap"
            element={
              <Suspense fallback={<Skeleton active paragraph={{ rows: 10 }} style={{ padding: 24 }} />}>
                <StockAssetTreemapPage />
              </Suspense>
            }
          />
          <Route
            path="/stock/assets/:assetId/flow"
            element={
              <Suspense fallback={<Skeleton active paragraph={{ rows: 10 }} style={{ padding: 24 }} />}>
                <StockAssetFlowPage />
              </Suspense>
            }
          />
          <Route path="/formx" element={<FormxListPage />} />
          <Route path="/formx/:id/field/:fieldId" element={<FormxFieldDataPage />} />
          <Route path="/formx/:id/:tab?" element={<FormxDetailPage />} />
          <Route path="/sourcing" element={<SourcingPage />} />
          <Route path="/users" element={<UserListPage />} />
          <Route path="/users/:id/:tab?" element={<UserDetailPage />} />
          <Route path="/orgs" element={<OrgListPage />} />
          <Route element={<SuperAdminRoute />}>
            <Route path="/admin/configurations" element={<ConfigurationAdminPage />} />
            <Route path="/admin/exports" element={<ExportAccessPage />} />
            <Route path="/admin/super-admin" element={<SuperAdminHubPage />} />
            <Route path="/admin/super-admin/force-sync" element={<ForceSyncPage />} />
            <Route path="/admin/super-admin/system-config" element={<SystemConfigPage />} />
          </Route>
          <Route path="/payout" element={<PayoutInboxPage />} />
          <Route path="/payout/:sessionId" element={<AdminPayoutDetailPage />} />
          <Route path="/accountant/payouts" element={<PayoutInboxPage />} />
          <Route path="/accountant/payouts/:sessionId" element={<AccountantPayoutDetailPage />} />
          <Route path="/tools" element={<ToolsHubPage />} />
          <Route path="/tools/address-fixer" element={<AddressFixerPage />} />
          <Route path="/experimental" element={<ExperimentalHubPage />} />
          <Route path="/experimental/mobile" element={<ExperimentalMobileSimPage />} />
          <Route path="/experimental/time-range-slider" element={<TimeRangeSliderDemo />} />
          <Route path="/experimental/ward-select" element={<WardSelectDemo />} />
          <Route path="/experimental/cmpt" element={<TestComponent />} />
          <Route path="/experimental/location-tab" element={<LocationTabDesignPage />} />
          <Route path="/experimental/project-detail" element={<ProjectDetailDesignPage />} />
          <Route path="/experimental/sidebar" element={<SidebarDesignPage />} />
          <Route path="/experimental/server-report-export" element={<ServerReportExportPage />} />
          <Route path="/experimental/session-timeline" element={<SessionTimelineDesign />} />
          <Route path="/experimental/session-gantt" element={<SessionGanttDesign />} />
          <Route path="/experimental/location-session-cards" element={<LocationSessionCards />} />
          <Route path="/experimental/project-cards" element={<ProjectListCardView />} />
          <Route path="/experimental/import-mapper" element={<ExcelImportMapperPage />} />
          <Route path="/contacts" element={<ContactListPage />} />
          <Route path="/audit-log" element={<AuditLogListPage />} />
          <Route path="/projects/:projectId/assessments/new" element={<AssessmentBuilderPage />} />
          <Route path="/projects/:projectId/assessments/:assessmentId/edit" element={<AssessmentBuilderPage />} />
        </Route>
      </Route>
      {TEST_ERROR_ROUTE_ENABLED && (
        <Route
          path="/test-error"
          element={
            <ErrorPage
              code="500"
              title="Internal Server Error"
              subtitle="Đây là trang lỗi giả để test."
              detail={"Error: Intentional test crash\n    at TestErrorRoute (AppRouter.tsx)\n    at renderWithHooks\n    at mountIndeterminateComponent"}
            />
          }
        />
      )}
      <Route path="*" element={<ErrorPage />} />
      </Routes>
    </Suspense>
  );
}
